// Shared price colour-coding logic for Flipping Friends.
// Used by PricesFoundModal (scan result) and AdminLibrary (master DB view)
// so both surfaces stay in lock-step with the reseller business rules.
//
// Rules (compared on CASH-EQUIVALENT value — CEX voucher × 0.82):
//   GOLD  — the single best cash-equivalent offer
//   GREEN — ≥ 60% of the best (viable alternative)
//   RED   — < 60% of the best, or £0 (decline)
//   GREY  — no data for that buyer

export const CEX_VOUCHER_RATIO = 0.82;
export const VIABLE_THRESHOLD = 0.6;

// Normalise raw price into a Number (handles {value: n} shape from MongoDB).
export const toNumber = (v) => {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'object' && 'value' in v) return v.value == null ? null : Number(v.value);
  return Number(v);
};

// Convert a buyer key + nominal price to its cash-equivalent.
export const cashEquivalent = (buyerKey, value) => {
  if (value == null) return null;
  if (buyerKey === 'cex_voucher') return value * CEX_VOUCHER_RATIO;
  return value;
};

/**
 * Grade a set of prices across the 5 canonical buyers.
 * `prices` is an object like { musicmagpie: 2.5, webuybooks: 1.8, ... } — values
 * may be numbers, nulls, or {value} objects. Returns:
 *   {
 *     maxCashEquiv: number,
 *     threshold: number,
 *     bestKey: string | null,   // buyer with the best cash-equivalent
 *     bestValue: number | null, // nominal £ of the best
 *     grades: { [buyerKey]: 'gold' | 'green' | 'red' | 'grey' }
 *   }
 */
export const gradePrices = (prices) => {
  const keys = ['musicmagpie', 'webuybooks', 'worldofbooks', 'cex_cash', 'cex_voucher'];
  const values = Object.fromEntries(keys.map((k) => [k, toNumber(prices?.[k])]));
  const ces = Object.fromEntries(keys.map((k) => [k, cashEquivalent(k, values[k])]));

  let maxCashEquiv = 0;
  for (const k of keys) {
    if (ces[k] != null && ces[k] > maxCashEquiv) maxCashEquiv = ces[k];
  }
  const threshold = maxCashEquiv * VIABLE_THRESHOLD;
  const anyPositive = maxCashEquiv > 0;

  let bestKey = null;
  if (anyPositive) {
    bestKey = keys.find((k) => ces[k] === maxCashEquiv) || null;
  }

  const grades = {};
  for (const k of keys) {
    if (values[k] == null) {
      grades[k] = 'grey';
    } else if (!anyPositive || ces[k] === 0) {
      grades[k] = 'red';
    } else if (ces[k] === maxCashEquiv) {
      grades[k] = 'gold';
    } else if (ces[k] >= threshold) {
      grades[k] = 'green';
    } else {
      grades[k] = 'red';
    }
  }

  return {
    maxCashEquiv,
    threshold,
    bestKey,
    bestValue: bestKey ? values[bestKey] : null,
    grades,
  };
};

// Tailwind / inline-style colour tokens matching the dark brand palette.
export const GRADE_COLORS = {
  gold:  { accent: '#FFD700', bg: 'rgba(255,215,0,0.10)', text: '#FFD700' },
  green: { accent: '#00D26A', bg: 'rgba(0,210,106,0.08)', text: '#00D26A' },
  red:   { accent: '#E04A3F', bg: 'rgba(224,74,63,0.08)', text: '#E04A3F' },
  grey:  { accent: '#3a3a3a', bg: '#101010',              text: '#6a6a6a' },
};
